import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ConfiguracionFormulario,
  OpcionCampo,
  TipoCampoFormulario,
  ValoresFormulario,
} from 'src/app/compartido/formulario/utilidades/formulario.models';
import { deepCopy } from 'src/app/compartido/utilidades/objetos.utils';
import {
  CONFIGURACION_FICHA_CREAR_ORDEN,
  CONFIGURACION_FORMULARIO_CREAR_ORDEN,
  CONFIGURACION_FORMULARIO_ITEM_ORDEN,
  CONFIGURACION_FORMULARIO_FORMULA_ORDEN,
} from './utilidades/crear-orden.static';
import { ClientesService } from 'src/app/clientes/utilidades/clientes.service';
import {
  ClienteCrearDTO,
  ClienteDTO,
  DireccionEditarDTO,
} from 'src/app/clientes/utilidades/clientes.models';
import { AlertasService } from 'src/app/nucleo/utilidades/alertas.service';
import { debounceTime, firstValueFrom, Subject, Subscription, tap } from 'rxjs';
import { OrdenesService } from '../../utilidades/ordenes.service';
import {
  ItemOrdenCrearDTO,
  OrdenCrearDTO,
} from '../../utilidades/ordenes.models';
import {
  ErrorDesarrollo,
  ErrorFrontend,
} from 'src/app/compartido/utilidades/error.utils';
import { ActivatedRoute, Router } from '@angular/router';
import { obtenerCampo } from 'src/app/compartido/formulario/utilidades/formulario.utils';
import {
  Medicamento,
  MedicamentosService,
} from 'src/app/nucleo/utilidades/medicamentos.service';
import { AutenticacionService } from 'src/app/nucleo/autenticacion/autenticacion.service';
import { RolUsuario } from 'src/app/nucleo/autorizacion/autorizacion.models';
import { FormulariosService } from 'src/app/nucleo/utilidades/formularios.service';
import {
  stringVacío,
  valorNumérico,
} from 'src/app/compartido/utilidades/valores.utils';
import { DialogosService } from 'src/app/nucleo/utilidades/dialogos.service';
import { Orden } from '../../utilidades/orden.class';

@Component({
  selector: 'orden-crear-orden',
  templateUrl: './crear-orden.component.html',
  styleUrls: ['./crear-orden.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CrearOrdenComponent implements OnInit, OnDestroy {
  /* Suscripción */
  private readonly _suscripcion = new Subscription();

  /* Configuración ficha */
  protected configFicha = deepCopy(CONFIGURACION_FICHA_CREAR_ORDEN);

  /* Formulario de la orden */
  protected configFormulario: ConfiguracionFormulario = deepCopy(
    CONFIGURACION_FORMULARIO_CREAR_ORDEN,
  );
  protected validezFormulario: boolean = false;
  protected valorFormulario: ValoresFormulario | null = null;

  /* Formularios de los items */
  protected configFormulariosItems: ConfiguracionFormulario[] = [];
  protected valoresFormulariosItems: ValoresFormulario[] = [];
  protected validezFormulariosItems: boolean[] = [];
  protected get validezItems(): boolean {
    return this.validezFormulariosItems.every((validez) => validez);
  }

  /* Formularios de las fórmulas */
  protected configFormulariosFormulas: ConfiguracionFormulario[] = [];
  protected valoresFormulariosFormulas: ValoresFormulario[] = [];
  protected validezFormulariosFormulas: boolean[] = [];
  protected get validezFormulas(): boolean {
    return (
      this.validezFormulariosFormulas.every((validez) => validez) &&
      this.valoresFormulariosFormulas.length >= this._minimoFormulas
    );
  }
  private _minimoFormulas: number = 0;

  /* Cargando */
  protected cargando: boolean = false;

  constructor(
    private readonly _autenticacionService: AutenticacionService,
    private readonly _dialogosService: DialogosService,
    private readonly _formulariosService: FormulariosService,
    private readonly _alertasService: AlertasService,
    private readonly _clientesService: ClientesService,
    private readonly _ordenesService: OrdenesService,
    private readonly _medicamentosService: MedicamentosService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
  ) {}

  ngOnInit(): void {
    const form = this.configFormulario;

    // Poblar opciones de organización
    this._formulariosService.configurarOpcionesOrganizacion$(form);

    // Poblar opciones de origen
    this._formulariosService
      .configurarOpcionesOrigen$(form, false, true, true)
      .then((suscripcion: Subscription) => {
        this._suscripcion.add(suscripcion);

        // Escuchar query params
        const campoOrg = obtenerCampo(form, 'organizacion')!;
        this._formulariosService.agregarValorIn(form, ['organizacion']);
        firstValueFrom(this._route.queryParams).then((params) => {
          const orgId = params['organizacion'];
          if (orgId === undefined) return;
          campoOrg.valorIn!.next(valorNumérico(orgId));
        });
      });

    // Configurar formulario de dirección
    this._formulariosService.configurarFormularioDireccion$(form);

    // Poblar opciones de identificación dinámicamente
    const campoIdentificacion = obtenerCampo(form, 'identificacion')!;
    if (campoIdentificacion.tipo !== TipoCampoFormulario.AUTOCOMPLETE)
      throw new ErrorDesarrollo(
        'El campo cliente debe ser de tipo AUTOCOMPLETE',
      );
    campoIdentificacion.valorIn = new Subject<number>();
    campoIdentificacion.valorOut = new Subject<string>();
    campoIdentificacion.opcionesIn = new Subject<
      OpcionCampo<string | number>[]
    >();
    campoIdentificacion.opcionOut = new Subject<OpcionCampo<string | number>>();
    const escucharIdentificacion = campoIdentificacion.valorOut!.subscribe(
      (valor) => this._poblarOpcionesIdentificacion$(valor),
    );
    this._suscripcion.add(escucharIdentificacion);

    // Poblar datos al seleccionar una identificación
    this._formulariosService.agregarValorIn(form, [
      'nombre',
      'celular',
      'telefono',
      'correo',
      'direccion',
      'direccion_formateada',
      'detalles',
      'barrio',
      'localidad',
      'ciudad',
      'departamento',
      'latitud',
      'longitud',
    ]);
    this._suscripcion.add(
      campoIdentificacion.opcionOut!.subscribe((opcion) =>
        this._poblarCliente$(opcion.extra as number),
      ),
    );

    // Editar formulario si el usuario es cliente
    this._autenticacionService.usuario$.then((usuario) => {
      if (usuario!.rol !== RolUsuario.CLIENTE) return;
      const cliente = usuario!.cliente!;
      escucharIdentificacion.unsubscribe();
      campoIdentificacion.opcionOut!.next({
        nombre: `${cliente.nombre} - ${cliente.identificacion}`,
        valor: cliente.identificacion,
        extra: cliente.id,
      });
      campoIdentificacion.valorIn!.next(cliente.id);
      campoIdentificacion.readonly = true;
      obtenerCampo(form, 'referencia')!.oculto = true;
      this._minimoFormulas = 1;
    });

    // Configurar si es express
    const campoPrioridad = obtenerCampo(form, 'prioridad')!;
    campoPrioridad.valorIn = new Subject<string>();
    if (campoPrioridad.tipo !== TipoCampoFormulario.OPCION_MULTIPLE)
      throw new ErrorDesarrollo(
        'El campo prioridad debe ser de tipo OPCION_MULTIPLE',
      );
    campoPrioridad.opcionesIn = new Subject<OpcionCampo<string | number>[]>();
    firstValueFrom(this._route.url).then((url) => {
      if (url.some((segment) => segment.path === 'express')) {
        campoPrioridad.opcionesIn!.next([
          { nombre: 'Express (Urgente)', valor: 'express' },
        ]);
        campoPrioridad.valorIn!.next('express');
        campoPrioridad.readonly = true;
      }
    });
  }

  ngOnDestroy(): void {
    this._suscripcion.unsubscribe();
  }

  /**
   * Puebla las opciones del campo de cliente con los clientes que coinciden con la búsqueda
   * @param {string} busqueda - Valor de la identificación a buscar
   * @author Juan Corral
   */
  private async _poblarOpcionesIdentificacion$(
    busqueda: string,
  ): Promise<void> {
    const idOrg = this.valorFormulario?.['organizacion'];
    if (!idOrg) {
      this._alertasService.alertarError('Seleccione una organización primero');
      return;
    }
    const clientes = await this._clientesService.obtenerClientes$({
      search: busqueda,
      organizacion: idOrg,
    });
    const campoIdentificacion = obtenerCampo(
      this.configFormulario,
      'identificacion',
    )!;
    if (campoIdentificacion.tipo !== TipoCampoFormulario.AUTOCOMPLETE)
      throw new ErrorDesarrollo(
        'El campo cliente debe ser de tipo AUTOCOMPLETE',
      );
    campoIdentificacion.opcionesIn!.next(
      clientes.results.map((cliente: ClienteDTO) => ({
        nombre: `${cliente.nombre} - ${cliente.identificacion}`,
        valor: cliente.identificacion,
        extra: cliente.id,
      })),
    );
  }

  /**
   * Puebla los campos del formulario con los datos del cliente seleccionado.
   * @param {number} id - Id del cliente a poblar en el formulario.
   * @author Juan Corral
   */
  private async _poblarCliente$(id: number): Promise<void> {
    const cliente = await this._clientesService
      .obtenerCliente$(id)
      .catch((error: ErrorFrontend) => {
        this._alertasService.alertarError('Error: ' + error.message);
        return null;
      });
    if (!cliente) return;

    const campoNombre = obtenerCampo(this.configFormulario, 'nombre')!;
    campoNombre.valorIn!.next(cliente.nombre);

    const campoCelular = obtenerCampo(this.configFormulario, 'celular')!;
    campoCelular.valorIn!.next(cliente.celular);

    const campoTelefono = obtenerCampo(this.configFormulario, 'telefono')!;
    campoTelefono.valorIn!.next(cliente.telefono);

    const campoCorreo = obtenerCampo(this.configFormulario, 'correo')!;
    campoCorreo.valorIn!.next(cliente.correo);

    if (cliente.direcciones.length === 0) return;
    const direccion = cliente.direcciones[cliente.direcciones.length - 1];

    const campoDireccion = obtenerCampo(this.configFormulario, 'direccion')!;
    campoDireccion.valorIn!.next(direccion.direccion);

    const campoDireccionFormateada = obtenerCampo(
      this.configFormulario,
      'direccion_formateada',
    )!;
    campoDireccionFormateada.valorIn!.next(direccion.direccion_formateada);

    const campoDetalles = obtenerCampo(this.configFormulario, 'detalles')!;
    campoDetalles.valorIn!.next(direccion.detalles);

    const campoBarrio = obtenerCampo(this.configFormulario, 'barrio')!;
    campoBarrio.valorIn!.next(direccion.barrio);

    const campoLocalidad = obtenerCampo(this.configFormulario, 'localidad')!;
    campoLocalidad.valorIn!.next(direccion.localidad);

    const campoCiudad = obtenerCampo(this.configFormulario, 'ciudad')!;
    campoCiudad.valorIn!.next(direccion.ciudad);

    const campoDepartamento = obtenerCampo(
      this.configFormulario,
      'departamento',
    )!;
    campoDepartamento.valorIn!.next(direccion.departamento);

    const campoLatitud = obtenerCampo(this.configFormulario, 'latitud')!;
    campoLatitud.valorIn!.next(direccion.latitud);

    const campoLongitud = obtenerCampo(this.configFormulario, 'longitud')!;
    campoLongitud.valorIn!.next(direccion.longitud);
  }

  /**
   * Agrega un formulario de item a la lista de formularios de items de la orden.
   * @author Juan Corral
   */
  protected agregarItem(): void {
    const configuracion = deepCopy(CONFIGURACION_FORMULARIO_ITEM_ORDEN);

    // Agregar opciones de productos
    const campoProducto = obtenerCampo(configuracion, 'producto')!;
    if (campoProducto.tipo !== TipoCampoFormulario.AUTOCOMPLETE)
      throw new ErrorDesarrollo(
        'El campo producto debe ser de tipo AUTOCOMPLETE',
      );
    campoProducto.opcionesIn = new Subject<OpcionCampo<string | number>[]>();
    campoProducto.valorOut = new Subject<string>();
    const escucharValor = campoProducto.valorOut
      .pipe(
        debounceTime(100),
        tap((valor) => {
          this._medicamentosService
            .buscarMedicamentos$(valor.toUpperCase())
            .then((medicamentos) =>
              campoProducto.opcionesIn!.next(
                medicamentos.map((medicamento: Medicamento) => ({
                  nombre: `${medicamento.producto} - ${medicamento.descripcionatc}`,
                  valor: medicamento.producto,
                })),
              ),
            );
        }),
      )
      .subscribe();
    this._suscripcion.add(escucharValor);

    this.configFormulariosItems.push(configuracion);
    this.valoresFormulariosItems.push({});
    this.validezFormulariosItems.push(false);
    this._cdr.markForCheck();
  }

  /**
   * Agrega un formulario de fórmula a la lista de formularios de fórmulas de la orden.
   * @author Juan Corral
   */
  protected agregarFormula(): void {
    const configuracion = deepCopy(CONFIGURACION_FORMULARIO_FORMULA_ORDEN);
    this.configFormulariosFormulas.push(configuracion);
    this.valoresFormulariosFormulas.push({});
    this.validezFormulariosFormulas.push(false);
    this._cdr.markForCheck();
  }

  /**
   * Elimina un formulario de item de la lista de formularios de items de la orden.
   * @param {number} index - Índice del formulario a eliminar.
   * @author Juan Corral
   */
  protected eliminarItem(index: number): void {
    this.configFormulariosItems.splice(index, 1);
    this.valoresFormulariosItems.splice(index, 1);
    this.validezFormulariosItems.splice(index, 1);
    this._cdr.markForCheck();
  }

  /**
   * Elimina un formulario de fórmula de la lista de formularios de fórmulas de la orden.
   * @param {number} index - Índice del formulario a eliminar.
   * @author Juan Corral
   */
  protected eliminarFormula(index: number): void {
    this.configFormulariosFormulas.splice(index, 1);
    this.valoresFormulariosFormulas.splice(index, 1);
    this.validezFormulariosFormulas.splice(index, 1);
    this._cdr.markForCheck();
  }

  /**
   * Crea una orden con los datos del formulario.
   * @author Juan Corral
   */
  protected crearOrden(): void {
    if (
      !this.validezFormulario ||
      !this.validezItems ||
      !this.validezFormulas
    ) {
      this._alertasService.alertarError('Complete todos los campos');
      return;
    }
    const valores = this.valorFormulario!;

    // Construir dirección
    const direccion: DireccionEditarDTO = {
      direccion: valores['direccion']!,
      direccion_formateada: valores['direccion_formateada'] ?? undefined,
      detalles: valores['detalles'] ?? undefined,
      barrio: valores['barrio'] ?? undefined,
      localidad: valores['localidad'] ?? undefined,
      ciudad: valores['ciudad']!,
      departamento: valores['departamento']!,
      latitud: valores['latitud'] ?? undefined,
      longitud: valores['longitud'] ?? undefined,
      creacion_manual: true,
    };

    // Construir cliente
    const cliente: ClienteCrearDTO = {
      tipo_identificacion: undefined,
      identificacion: valores['identificacion']!,
      nombre: valores['nombre']!,
      telefono: valores['telefono'] ?? undefined,
      celular: valores['celular'] ?? undefined,
      correo: valores['correo'] ?? undefined,
      organizacion: valores['organizacion']!,
    };

    // Construir items
    const items: ItemOrdenCrearDTO[] = this.valoresFormulariosItems.map(
      (valoresItem) => ({
        producto: {
          nombre: valoresItem['producto'],
          id_externo: undefined,
        },
        cantidad: valoresItem['cantidad'],
      }),
    );

    // Construir fórmulas
    const formulas = this.valoresFormulariosFormulas.map((valoresFormula) => ({
      referencia: valoresFormula['referencia'],
      archivo: valoresFormula['archivo'].files[0] as File,
    }));

    // Construir orden
    const orden: OrdenCrearDTO = {
      enumeracion: undefined,
      referencia: valores['referencia'] ?? undefined,
      tipo: valores['tipo'] ?? undefined,
      prioridad: valores['prioridad'] ?? undefined,
      origen: valores['origen'] ?? undefined,
      centro_costo: !stringVacío(valores['centro_costo'])
        ? valores['centro_costo']
        : undefined,
      servicio: !stringVacío(valores['servicio'])
        ? valores['servicio']
        : undefined,
      recaudo: valores['recaudo'] ?? undefined,
      direccion,
      cliente,
      items,
      formulas,
      notas: valores['notas'] ?? undefined,
    };

    // Crear orden
    this._ordenesService
      .crearOrden$(orden)
      .then((orden: Orden) => {
        this._dialogosService
          .abrirDialogo$(
            '¡Orden Creada!',
            `La orden ${orden.id} ha sido creada con éxito.`,
          )
          .then(async () => {
            // Imprimir etiqueta, si se requiere
            const por_hoja = orden.origen?.etiquetas_por_hoja ?? 0;
            if (!orden.sinDomicilio && por_hoja === 1) {
              const imprimir = await this._dialogosService.abrirDialogo$(
                '¿Imprimir la etiqueta de la orden?',
                'Puedes imprimir la etiqueta de la orden ahora o más tarde.',
                'Imprimir',
                'Más tarde',
              );
              if (imprimir)
                this._ordenesService.descargarEtiquetas$({
                  ids: orden.id.toString(),
                });
            }

            this._router.navigate(['ordenes']);
          });
      })
      .catch((error: ErrorFrontend) =>
        this._alertasService.alertarError('Error: ' + error.message),
      );
  }
}
